import type { FetchQueryOptions, QueryClient } from '@tanstack/svelte-query';
import type { ShopContext } from '$lib/context.js';
import { hasOwnProperty } from '$lib/util/util.js';
import type { LoadEvent } from '@sveltejs/kit';

export function prefetchQueries<PLE extends LoadEvent>(
    queryOptions: (
        fetchImpl: typeof fetch,
        shopContext: ShopContext,
        params: PLE['params'],
        event: PLE
    ) => FetchQueryOptions<any, unknown, any, any>[] | Promise<FetchQueryOptions<any, unknown, any, any>[]>
): (
    event: Pick<
        LoadEvent<
            Partial<Record<string, string>>,
            Record<string, unknown>,
            {
                queryClient: QueryClient;
                shopContext: ShopContext;
            }
        >,
        'params' | 'parent' | 'fetch' | 'data'
    >
) => Promise<PLE['data']> {
    return async (event) => {
        const { params, parent, fetch, data } = event;
        const { queryClient, shopContext } = await parent();

        const queryOptionsList = await queryOptions(fetch, shopContext, params as PLE['params'], event as PLE);

        await Promise.allSettled(
            queryOptionsList
                .filter(
                    (options) =>
                        !hasOwnProperty(options, 'enabled') || typeof options.enabled !== 'boolean' || options.enabled
                )
                .map((options) => queryClient.prefetchQuery(options))
        );

        return { ...data };
    };
}
